import { useState } from "react";
import { ClockMoment } from "../../../../../api/moments";
import { extractApiErrorMessage } from "../../../../../utils/util";
import useKlydo from "../../../../../components/hooks/useKlydo";
import DSBox from "../../../../system-design/DSBox";
import DSCard from "../../../../system-design/DSCard";
import DSCardContent from "../../../../system-design/DSCardContent";
import DSSectionHeading from "../../../../system-design/DSSectionHeading";
import DSTypography from "../../../../system-design/DSTypography";
import { sectionCardSx } from "../../../../system-design/sectionCardSx";
import { DSBaseTable, DSTableCell } from "../../../../system-design/Table";
import { Column } from "../../../../system-design/Table/DSBaseTable/components/DSBaseTable";
import {
  useDeleteDeviceMomentMutation,
  useDeviceMomentsQuery,
} from "../../../hooks/useDeviceMomentsQuery";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatMomentDate = ({ month, day, year }: ClockMoment["date"]) =>
  `${MONTHS[month - 1]} ${day}${year ? `, ${year}` : ""}`;

// 20261025 -> "Oct 25, 2026"
const formatDateKey = (dateKey?: number) => {
  if (!dateKey) return "Not yet";
  const year = Math.floor(dateKey / 10000);
  const month = Math.floor((dateKey % 10000) / 100);
  const day = dateKey % 100;
  return formatMomentDate({ month, day, year });
};

const KlydoName = ({ klydoId }: { klydoId: string }) => {
  const { data: klydo } = useKlydo(klydoId);
  return <>{klydo?.name ?? klydoId}</>;
};

const columns: Column<ClockMoment>[] = [
  { field: "date", label: "Date", sortable: false },
  { field: "title", label: "Title", sortable: false },
  { field: "occasionKey", label: "Occasion", sortable: false },
  { field: "klydoId", label: "Klydo", sortable: false },
  { field: "pendulumLogoUrl", label: "Pendulum", sortable: false },
  { field: "lastDeliveredDateKey", label: "Last shown", sortable: false },
];

// The moments the clock's owner created in the app: read-only list plus delete.
const DeviceMomentsCard = ({ deviceId }: { deviceId: string }) => {
  const { data: moments, isLoading, error } = useDeviceMomentsQuery(deviceId);
  const {
    mutate: deleteMoment,
    error: deleteError,
    variables: deletingMomentId,
    isPending: isDeleting,
  } = useDeleteDeviceMomentMutation(deviceId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <DSBox sx={{ mb: 2 }}>
      <DSCard sx={sectionCardSx}>
        <DSSectionHeading>
          {`Moments${moments?.length ? ` (${moments.length})` : ""}`}
        </DSSectionHeading>
        <DSCardContent>
          {error ? (
            <DSBox
              sx={{ textAlign: "center", padding: "20px", color: "error.main" }}
            >
              {extractApiErrorMessage(error, "Failed to load moments")}
            </DSBox>
          ) : isLoading ? (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              Loading moments...
            </DSBox>
          ) : moments && moments.length > 0 ? (
            <DSBaseTable
              data={moments}
              columns={columns}
              rowBuilder={(moment) => (
                <>
                  <DSTableCell key="date">
                    {formatMomentDate(moment.date)}
                    {moment.recurrence === "yearly" ? " (yearly)" : ""}
                  </DSTableCell>
                  <DSTableCell
                    key="title"
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setExpandedId(expandedId === moment.id ? null : moment.id)
                    }
                  >
                    {moment.title}
                    {expandedId === moment.id && moment.description ? (
                      <DSTypography variant="caption" sx={{ display: "block" }}>
                        {moment.description}
                      </DSTypography>
                    ) : null}
                  </DSTableCell>
                  <DSTableCell key="occasion">{moment.occasionKey}</DSTableCell>
                  <DSTableCell key="klydo">
                    {moment.klydoStatus === "pending" ? (
                      <DSTypography variant="caption">Choosing…</DSTypography>
                    ) : (
                      <KlydoName klydoId={moment.klydoId} />
                    )}
                  </DSTableCell>
                  <DSTableCell key="pendulum">
                    {moment.logoStatus === "pending" ||
                    moment.logoStatus === "generating" ? (
                      <DSTypography variant="caption">Generating…</DSTypography>
                    ) : moment.pendulumLogoUrl ? (
                      <img
                        width={35}
                        src={moment.pendulumLogoUrl}
                        alt={moment.logoSource ?? "pendulum"}
                        title={
                          moment.logoSource === "occasion"
                            ? "Default occasion logo (generation failed)"
                            : "Generated for this moment"
                        }
                        style={{ borderRadius: "50%" }}
                      />
                    ) : (
                      "None"
                    )}
                  </DSTableCell>
                  <DSTableCell key="lastShown">
                    {formatDateKey(moment.lastDeliveredDateKey)}
                  </DSTableCell>
                </>
              )}
              onPressDelete={(moment) => deleteMoment(moment.id)}
              deletingRowId={isDeleting ? (deletingMomentId ?? null) : null}
              getRowId={(moment) => moment.id}
              defaultRowsPerPage={5}
            />
          ) : (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              No moments
            </DSBox>
          )}
          {deleteError && (
            <DSBox sx={{ color: "error.main", marginTop: "10px" }}>
              {extractApiErrorMessage(
                deleteError,
                "Failed to delete the moment",
              )}
            </DSBox>
          )}
        </DSCardContent>
      </DSCard>
    </DSBox>
  );
};

export default DeviceMomentsCard;
